import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";
import styles from "./teacher-list.module.css";
import { Avatar, Button, Dropdown, Menu, Popconfirm, Tooltip, Typography } from "antd";
import { LeftOutlined, MoreOutlined } from "@ant-design/icons";
import { PAGE_LIMIT } from "../../../../constants/common";
import { useLocation, useNavigate } from "react-router-dom";
import CustomTextfield from "../../../../common/input/custom-textfield/custom-textfield";
import SVGIcon from "../../../../constants/SVGIcon";
import CustomTable from "../../../../common/custom-table/custom-table";
import RoutesConstant from "../../../../constants/routes";
import CustomTag from "../../../../common/custom-tag/custom-tag";
import { toast } from "react-toastify";
import {
  deleteTeacher,
  getAcademicDepartments,
  getTeachersList,
} from "../../../../services/teachers";

const ALL = "All";
const allObj = { label: ALL, value: ALL.toLowerCase() };

const TeacherList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const id = params.get("id");
  const [filterAble, setFilterAble] = useState([]);
  const [checkedFilters, setCheckedFilters] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [pagination, setPagination] = useState({
    totalPage: 0,
    currentPage: 1,
    totalRecords: 0,
  });
  const [loadingTeachers, setLoadingTeachers] = useState(false);

  const fetchFilterAbleData = useCallback(async () => {
    try {
      const resp = await getAcademicDepartments();
      const departments = resp?.data?.departments;

      const createFilterObject = departments?.flatMap((item, idx) => {
        const d = {
          label: item?.department_name,
          value: item?.department_id,
        };
        return !idx ? [allObj, d] : [d];
      });

      const preSelected = createFilterObject.filter((i) => i.value === id);
      setFilterAble(createFilterObject);
      setCheckedFilters(preSelected ?? []);
    } catch (err) {
      toast.error("Error Fetching Departments");
    }
  }, [id]);

  useEffect(() => {
    fetchFilterAbleData();
  }, [id, fetchFilterAbleData]);

  const getUniqueValues = (arr, key) =>
    Array.from(new Set(arr?.map(item => item[key])));

  const columns = useMemo(
    () => [
      { title: "Emp ID.", dataIndex: "user_name", key: "user_name" },
      {
        title: "Teacher Name",
        dataIndex: "full_name",
        key: "full_name",
        render: (_, record) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <Avatar
              src={record.profile_picture}
              style={{ marginRight: 8 }}
              size={40}
            >
              {record.full_name?.[0]}
            </Avatar>
            {record.full_name}
          </div>
        ),
      },
      { title: "Gender", dataIndex: "gender", key: "gender" },
      {
        title: "Expert In",
        dataIndex: "subjects_taught",
        key: "subjects_taught",
        render: (value = []) => {
          const uniqueSubjects = getUniqueValues(value, "subject_name");
          const extraSubjects = uniqueSubjects?.length > 1 ? uniqueSubjects?.slice(1) : [];

          return (
            <div className={styles.subjectsValue}>
              {uniqueSubjects[0] ?? "-"}
              {extraSubjects?.length > 0 && (
                <Tooltip 
                  title={extraSubjects?.join(", ")}
                  color="var(--primary-color)"
                >
                  <span className={styles.subjectMoreTooltip}>
                    +{extraSubjects?.length}
                  </span>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: "Department",
        dataIndex: "subjects_taught",
        key: "subjects_taught",
        render: (value = []) => {
          const uniqueDepartments = getUniqueValues(value, "department_name");
          const extraDepartments = uniqueDepartments?.length > 1 ? uniqueDepartments?.slice(1) : [];

          return (
            <div className={styles.subjectsValue}>
              {uniqueDepartments[0] ?? "-"}
              {extraDepartments?.length > 0 && (
                <Tooltip 
                  title={extraDepartments?.join(", ")}
                  color="var(--primary-color)"
                >
                  <span className={styles.subjectMoreTooltip}>
                    +{extraDepartments?.length}
                  </span>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: "Experience",
        dataIndex: "years_of_experience",
        key: "years_of_experience",
        render: (value) => <p>{value ? value + " years" : "-"}</p>,
      },
      {
        title: "",
        dataIndex: "actions",
        key: "actions",
        width: 50,
        align: "center",
        render: (_, record) => {
          return (
            <Dropdown
              overlay={<Menu items={menuItems(record)} />}
              trigger={["click"]}
            >
              <Button type="text" icon={<MoreOutlined />} />
            </Dropdown>
          );
        },
      },
    ],
    [filterAble]
  );

  const menuItems = (record) => [
    {
      key: "1",
      label: "View Details",
      onClick: () =>
        navigate(`${RoutesConstant.TEACHER_DETAILS}?id=${record?.id}`),
    },
    {
      key: "2",
      label: "Edit",
      onClick: () =>
        navigate(`${RoutesConstant.TEACHER_FORM}?uid=${record?.id}`),
    },
    {
      key: "3",
      label: (
        <Popconfirm
          title={`Are you sure you want to delete ${record?.full_name}?`}
          onConfirm={() => handleDeleteTeacher(record?.id)}
          okText="Yes"
          cancelText="No"
        >
          <span>Delete</span>
        </Popconfirm>
      ),
    },
  ];

  const fetchData = useCallback(async () => {
    try {
      if (filterAble?.length) {
        const payload = {
          page: pagination.currentPage,
          pageSize: PAGE_LIMIT,
        };

        const filters = checkedFilters?.flatMap((i) => {
          if (i.value !== ALL?.toLowerCase()) return [i.value];
          return [];
        });

        payload.department_id = filters;

        setLoadingTeachers(true);
        const resp = await getTeachersList(payload);

        const data = resp?.data?.data;
        setPagination({
          currentPage: data?.currentPage,
          totalPage: data?.totalPages,
          totalRecords: data?.totalRecords,
        });
        setTeachers(data?.teachers);
      }
    } catch (err) {
      toast.error("Error Filtering Data");
    } finally {
      setLoadingTeachers(false);
    }
  }, [checkedFilters, filterAble, pagination.currentPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteTeacher = async (teacherId) => {
    try {
      setLoadingTeachers(true);
      await deleteTeacher(teacherId);
      await fetchData();
      toast.success("Teacher is deleted");
    } catch (err) {
      toast.error("Failed to delete teacher");
    } finally {
      setLoadingTeachers(false);
    }
  };

  const handleToggleChecked = (item) => {
    if (item.value === ALL.toLowerCase()) {
      setCheckedFilters((pre) =>
        filterAble?.length === pre?.length ? [] : filterAble
      );
    } else {
      setCheckedFilters((pre) =>
        pre?.find((i) => i.value === item?.value)
          ? pre.filter(
              (i) => ![item.value, ALL.toLowerCase()].includes(i.value)
            )
          : filterAble.length - pre.length === 2
          ? [allObj].concat(pre, item)
          : pre.concat(item)
      );
    }
    setPagination({ currentPage: 1, totalPage: 0, totalRecords: 0 });
  };

  return (
    <div className={styles.main_container}>
      <div className={styles.leftSection} onClick={() => navigate(-1)}>
        <LeftOutlined />
        <Typography className={styles.navTitle}>Teachers</Typography>
      </div>
      <div className={styles.title_container}>
        <Typography className={styles.title}>Teachers</Typography>
        <div className={styles.filter_container}>
          <CustomTextfield
            id={"search"}
            type={"text"}
            className={styles.inputField}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            suffix={
              <SVGIcon
                name={"search-icon"}
                width={"24"}
                height={"24"}
                className="anticon"
                stroke={"#667085"}
              />
            }
            placeholder={"Search ..."}
          />
        </div>
      </div>
      <div className={styles.filter_pills_container}>
        {filterAble?.map((item, index) => {
          const isChecked = checkedFilters.find((i) => i.value === item.value);
          return (
            <CustomTag
              key={index}
              text={item?.label}
              color={isChecked ? "#fff" : "rgba(57, 181, 74, 1)"}
              borderColor={isChecked ? "#fff" : "rgba(57, 181, 74, 1)"}
              background={isChecked ? "rgba(57, 181, 74, 1)" : "#fff"}
              borderRadius={"13px"}
              minWidth={"40px"}
              height={styles.tag_style}
              ticked={isChecked}
              cursor={"pointer"}
              name={item.label}
              handleToggleChecked={() => handleToggleChecked(item)}
            />
          );
        })}
      </div>
      <CustomTable
        columns={columns}
        dataSource={teachers}
        collapsible={false}
        pagination={true}
        showTotalText={true}
        pageSize={PAGE_LIMIT}
        loading={loadingTeachers}
        currentPage={pagination.currentPage}
        total={pagination.totalRecords}
        handlePageChange={(val) =>
          setPagination((pre) => ({ ...pre, currentPage: val }))
        }
      />
    </div>
  );
};

export default TeacherList;
